import { parseChoice } from './choice';
import type { AgentMessage, AgentSession, AgentUnreadResultKind } from './model';

const CONFIRMATION_WIDGET =
  /"type"\s*:\s*"(?:choice|choices|confirm|multi_choice|department_choice|change_set_approval)"/;

export function parseUnreadResultKind(value: unknown): AgentUnreadResultKind | undefined {
  return value === 'completed' || value === 'awaiting_confirmation' || value === 'failed'
    ? value
    : undefined;
}

export function payloadNeedsConfirmation(payload: unknown): boolean {
  if (parseChoice(payload)) return true;
  if (payload == null) return false;
  if (typeof payload === 'string') return CONFIRMATION_WIDGET.test(payload);
  try {
    return CONFIRMATION_WIDGET.test(JSON.stringify(payload));
  } catch {
    return false;
  }
}

export function liveUnreadKind(
  messages: readonly Pick<AgentMessage, 'role' | 'status' | 'ui_payload'>[] | undefined,
): AgentUnreadResultKind | undefined {
  if (!messages?.length) return undefined;
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message.role !== 'agent') continue;
    if (message.status === 'failed') return 'failed';
    if (payloadNeedsConfirmation(message.ui_payload)) return 'awaiting_confirmation';
    return undefined;
  }
  return undefined;
}

export function resolveUnreadKind(input: {
  unread: boolean;
  persisted?: AgentUnreadResultKind | null;
  live?: AgentUnreadResultKind;
}): AgentUnreadResultKind | undefined {
  if (!input.unread) return undefined;
  if (input.live === 'failed') return 'failed';
  return input.live ?? input.persisted ?? 'completed';
}

export function collectUnreadKinds(
  sessions: readonly AgentSession[],
  messagesBySession: Readonly<Record<string, AgentMessage[]>>,
): Map<string, AgentUnreadResultKind> {
  const result = new Map<string, AgentUnreadResultKind>();
  for (const session of sessions) {
    const kind = resolveUnreadKind({
      unread: session.has_unread_agent_result,
      persisted: session.unread_agent_result_kind,
      live: liveUnreadKind(messagesBySession[session.id]),
    });
    if (kind) result.set(session.id, kind);
  }
  return result;
}

export function completedUnreadSessionId(
  kinds: ReadonlyMap<string, AgentUnreadResultKind>,
  except?: string,
): string | undefined {
  for (const [sessionId, kind] of kinds) {
    if (sessionId !== except && kind === 'completed') return sessionId;
  }
  return undefined;
}
